import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { FeatureIcon, type FeatureIconName } from '#bv/components/FeatureIcons'
import { vouchesChannelText } from '#bv/lib/replacementPolicy'

export const Route = createFileRoute('/faq')({
  component: FAQPage,
})

const FAQ_ICON_COLORS = ['#00bfff', '#a78bfa', '#8ecfff', '#c4b5fd', '#60a5fa'] as const

const faqSections: { title: string; icon: FeatureIconName; faqs: { q: string; a: string }[] }[] = [
  {
    title: 'Orders & Delivery',
    icon: 'zap',
    faqs: [
      {
        q: 'How fast is delivery?',
        a: 'Most digital products are delivered within 5 minutes of payment confirmation. Actual times can vary by time zone, product type, and manual review — gradual follower delivery, for example, may take 24–72 hours. Each product page lists the exact delivery window.',
      },
      {
        q: 'How will I receive my order?',
        a: 'Digital credentials and product details are sent to the email address associated with your payment. Check your inbox (and spam folder) immediately after purchase.',
      },
      {
        q: 'Do you deliver 24/7?',
        a: 'Yes — our delivery systems are fully automated and run around the clock, including weekends and holidays. Fulfillment speed can still vary by time zone and product.',
      },
      {
        q: 'Can I track my order?',
        a: 'After purchase, you\'ll receive a confirmation email with your order ID. You can use this to contact support if needed.',
      },
    ],
  },
  {
    title: 'Payments & Security',
    icon: 'lock',
    faqs: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit/debit cards (Visa, Mastercard, Amex) and digital wallets through MoneyMotion. All transactions are SSL encrypted.',
      },
      {
        q: 'Is checkout safe?',
        a: 'Absolutely. All payments are processed by MoneyMotion. Your card information never touches our servers.',
      },
      {
        q: 'Will I see "PulseDigital" on my bank statement?',
        a: 'Charges may appear under "PulseDigital" or our payment processor\'s descriptor. Contact us if you need clarification for any transaction.',
      },
    ],
  },
  {
    title: 'Products & Accounts',
    icon: 'package',
    faqs: [
      {
        q: 'Are accounts and products safe to use?',
        a: 'We take every precaution to ensure quality, but results can vary depending on the product type and how it\'s used. Safety notes and usage guidelines are listed on individual product pages where relevant.',
      },
      {
        q: 'What if my account stops working?',
        a: `After delivery, vouch in ${vouchesChannelText()} within 24 hours if the product worked when you tested it — that starts your warranty. If something breaks inside that window, open a ticket in Discord with your order ID (subject to verification and policy). If it never worked on first use, reply in your ticket before vouching.`,
      },
      {
        q: 'Can I change the password on accounts I purchase?',
        a: 'This depends on the product. Shared accounts generally cannot have passwords changed. Full private accounts allow full control. This is always specified on the product page.',
      },
      {
        q: 'Do Discord boosts expire?',
        a: 'Boosts are tied to the boosting account\'s Nitro subscription (typically 1 month). We offer refills if any boosts drop — just contact support.',
      },
    ],
  },
  {
    title: 'Refunds & Guarantees',
    icon: 'shield',
    faqs: [
      {
        q: 'What is your refund policy?',
        a: `We prioritise replacements over cash refunds. Post in ${vouchesChannelText()} within 24 hours of delivery to activate your warranty; your replacement window runs from that vouch (usually 24 hours for most products; longer for some FA listings — see Terms §2). Dead-on-arrival issues must be reported in your ticket before vouching. Cash refunds are rare.`,
      },
      {
        q: 'How do I request a replacement?',
        a: `Your warranty must be active (vouch posted in ${vouchesChannelText()} within 24 hours of delivery, unless you reported DOA in-ticket first). Then open or update a Discord support ticket with your order ID, vouch link or timestamp, and proof of the issue.`,
      },
      {
        q: 'What if I made an accidental purchase?',
        a: 'Contact us within 1 hour of purchase if the product has not yet been delivered. We\'ll review on a case-by-case basis.',
      },
    ],
  },
  {
    title: 'Discord & Community',
    icon: 'message',
    faqs: [
      {
        q: 'Do I need Discord to buy from PulseDigital?',
        a: 'No — you can purchase entirely through the website. Discord is optional but strongly recommended for faster support and exclusive deals.',
      },
      {
        q: 'Where can I leave a vouch or review?',
        a: `Post in our Discord ${vouchesChannelText()} channel within 24 hours of delivery with your order ID and product name. This activates your warranty and helps the community. If the product did not work on arrival, use your support ticket first — do not vouch a broken delivery.`,
      },
      {
        q: 'How do I get support?',
        a: 'Open a ticket in our Discord server for the fastest response. You can also use the contact form on this website.',
      },
    ],
  },
]

function FAQSection({ section, iconColor }: { section: typeof faqSections[0]; iconColor: string }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ background: `${iconColor}14`, border: `1px solid ${iconColor}30`, color: iconColor }}
        >
          <FeatureIcon name={section.icon} size={22} className="text-current" />
        </span>
        <h2 className="font-display text-2xl tracking-wide chrome-text">{section.title.toUpperCase()}</h2>
      </div>
      <div className="space-y-2">
        {section.faqs.map((faq, i) => (
          <div
            key={i}
            className="rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
            style={{
              background: openIndex === i ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${openIndex === i ? 'rgba(0,191,255,0.15)' : 'rgba(0,191,255,0.06)'}`,
            }}
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
          >
            <div className="flex items-center justify-between px-6 py-4">
              <span className="font-medium text-sm" style={{ color: '#f0f6ff' }}>{faq.q}</span>
              <span
                className="ml-4 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-transform duration-300"
                style={{
                  background: openIndex === i ? 'rgba(0, 191, 255,0.2)' : 'rgba(0,191,255,0.06)',
                  color: openIndex === i ? '#00bfff' : '#8ecfff',
                  transform: openIndex === i ? 'rotate(45deg)' : 'none',
                }}
              >+</span>
            </div>
            {openIndex === i && (
              <div className="px-6 pb-5">
                <p className="text-sm leading-relaxed" style={{ color: '#8ecfff' }}>{faq.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function FAQPage() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <div
        className="py-16 px-4 sm:px-6 relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(0, 191, 255,0.08) 0%, transparent 100%)',
          borderBottom: '1px solid rgba(0,191,255,0.06)',
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display text-5xl sm:text-7xl tracking-wide chrome-text mb-3">FAQ</h1>
          <p className="text-base" style={{ color: '#5a6d8a' }}>
            Everything you need to know about PulseDigital. Can't find your answer?{' '}
            <Link to="/contact" className="underline hover:text-white transition-colors" style={{ color: '#8ecfff' }}>
              Contact us
            </Link>.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 space-y-14">
        {faqSections.map((section, i) => (
          <FAQSection key={section.title} section={section} iconColor={FAQ_ICON_COLORS[i % FAQ_ICON_COLORS.length]} />
        ))}

        {/* Still have questions */}
        <div
          className="rounded-2xl p-8 text-center"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,191,255,0.08)' }}
        >
          <h3 className="font-display text-3xl tracking-wide chrome-text mb-3">STILL HAVE QUESTIONS?</h3>
          <p className="text-sm mb-6" style={{ color: '#8ecfff' }}>
            Our team is active in Discord and responds to every ticket.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://discord.gg/tGxhDfRkdE"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-discord rounded-xl px-6 py-3 text-sm"
            >
              Open Discord Ticket
            </a>
            <Link
              to="/contact"
              className="btn-chrome inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm"
            >
              Contact Form
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
